// A finished trip, read like a workout (ux/01 "Data first"): how long the
// shop took, the pace per item, and the splits — each category in the order
// the basket reached it. Everything comes from rows the list already keeps:
// `done_at` is when an item went in the basket, `created_by` who added it, and
// Finish trip soft-deletes the whole basket under one `deleted_at`, so past
// trips are those rows grouped by it (useItems.finishItems).

/** A gap between two ticks longer than this is not shopping (ticked at home, a second stop): it counts as nothing. */
export const IDLE_GAP_MS = 15 * 60_000
/** Shorter than this, a trip has no time worth showing: the hero is the item count. */
export const MIN_TIMED_MS = 60_000
/** "Than usual" needs this many earlier timed trips… */
export const MIN_USUAL_TRIPS = 3
/** …and looks back over at most this many. */
const USUAL_WINDOW = 10

type Tick = { category_id: string | null; done_at: string | null }

export interface Split {
  categoryId: string | null
  items: number
  /** The time from the tick before to each of this category's ticks. */
  ms: number
}

export interface PastTrip {
  finishedAt: number
  items: number
  /** Null when too short to time (MIN_TIMED_MS). */
  ms: number | null
}

export interface TripRecap {
  items: number
  ms: number | null
  /** Milliseconds per item, when the trip is timed. */
  perItem: number | null
  /** In the order the basket reached each category. */
  splits: Split[]
  /** The median of recent timed trips, when there are enough of them. */
  usualMs: number | null
  /** Who added what, most first; `memberId` null for a member since removed. */
  addedBy: { memberId: string | null; items: number }[]
  /** Who picked what (items.done_by, 046), most first; empty unless more than one person picked. */
  pickedBy: { memberId: string; items: number }[]
}

const tickTime = (t: Tick) => (t.done_at ? Date.parse(t.done_at) : NaN)

/** Shopping time over ticks in time order: each gap, idle ones counting as nothing. */
function activeGaps(times: number[]): number[] {
  return times.map((t, i) => {
    if (i === 0) return 0
    const gap = t - times[i - 1]
    return gap > IDLE_GAP_MS ? 0 : gap
  })
}

const timed = (ms: number) => (ms >= MIN_TIMED_MS ? ms : null)

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

const NO_TRIPS: ReadonlySet<number> = new Set()

/**
 * The finish times of trips deleted from Stats (trips.deleted_at, 046): their
 * rows stay in the history (From before, Running low) but are no trip.
 */
export function deletedFinishes(trips: readonly { finished_at: string | null; deleted_at?: string | null }[]): Set<number> {
  return new Set(trips.flatMap(t => (t.deleted_at && t.finished_at ? [Date.parse(t.finished_at)] : [])))
}

/**
 * Past trips from the item history (useItemHistory): bought rows grouped by
 * the Finish trip that cleared them, newest first.
 */
export function pastTrips(
  rows: { done: boolean; done_at: string | null; deleted_at: string | null }[],
  deleted: ReadonlySet<number> = NO_TRIPS,
): PastTrip[] {
  const byFinish = new Map<string, number[]>()
  for (const r of rows) {
    if (!r.done || !r.done_at || !r.deleted_at || deleted.has(Date.parse(r.deleted_at))) continue
    const t = Date.parse(r.done_at)
    if (Number.isNaN(t)) continue
    const list = byFinish.get(r.deleted_at)
    if (list) list.push(t)
    else byFinish.set(r.deleted_at, [t])
  }
  return [...byFinish].map(([finishedAt, times]) => {
    times.sort((a, b) => a - b)
    return { finishedAt: Date.parse(finishedAt), items: times.length, ms: timed(activeGaps(times).reduce((a, b) => a + b, 0)) }
  }).sort((a, b) => b.finishedAt - a.finishedAt)
}

/** The recap of the basket just finished, against the trips before it. */
export function tripRecap(bought: (Tick & { created_by: string | null; done_by?: string | null })[], past: PastTrip[]): TripRecap {
  const ticks = bought.filter(b => !Number.isNaN(tickTime(b))).sort((a, b) => tickTime(a) - tickTime(b))
  const gaps = activeGaps(ticks.map(tickTime))

  const splits: Split[] = []
  const byCategory = new Map<string | null, Split>()
  ticks.forEach((t, i) => {
    let split = byCategory.get(t.category_id)
    if (!split) {
      split = { categoryId: t.category_id, items: 0, ms: 0 }
      byCategory.set(t.category_id, split)
      splits.push(split)
    }
    split.items += 1
    split.ms += gaps[i]
  })
  // Rows without a tick time (written by an old client) still count as bought.
  const undated = bought.length - ticks.length
  if (undated > 0) {
    const other = byCategory.get(null) ?? { categoryId: null, items: 0, ms: 0 }
    if (!byCategory.has(null)) splits.push(other)
    other.items += undated
  }

  const ms = timed(gaps.reduce((a, b) => a + b, 0))
  const recent = past.flatMap(p => (p.ms === null ? [] : [p.ms])).slice(0, USUAL_WINDOW)

  const counts = new Map<string | null, number>()
  for (const b of bought) counts.set(b.created_by, (counts.get(b.created_by) ?? 0) + 1)
  const picks = new Map<string, number>()
  for (const b of bought) if (b.done_by) picks.set(b.done_by, (picks.get(b.done_by) ?? 0) + 1)

  return {
    items: bought.length,
    ms,
    perItem: ms === null ? null : ms / bought.length,
    splits,
    usualMs: recent.length >= MIN_USUAL_TRIPS ? median(recent) : null,
    addedBy: [...counts].map(([memberId, items]) => ({ memberId, items })).sort((a, b) => b.items - a.items),
    pickedBy: picks.size > 1 ? [...picks].map(([memberId, items]) => ({ memberId, items })).sort((a, b) => b.items - a.items) : [],
  }
}

/**
 * One finished trip read back from the item history: the rows its Finish trip
 * cleared (one deleted_at for the lot, on the phone and in the watch function),
 * against the trips before it.
 */
export function recapOfTrip(
  finishedAt: string,
  rows: (Tick & { done: boolean; deleted_at: string | null; created_by: string | null; done_by?: string | null })[],
  deleted: ReadonlySet<number> = NO_TRIPS,
): TripRecap {
  const at = Date.parse(finishedAt)
  return tripRecap(boughtIn(finishedAt, rows), pastTrips(rows, deleted).filter(t => t.finishedAt < at))
}

/** The history rows one Finish trip cleared: its basket. */
export function boughtIn<T extends { done: boolean; deleted_at: string | null }>(finishedAt: string, rows: readonly T[]): T[] {
  const at = Date.parse(finishedAt)
  return rows.filter(r => r.done && r.deleted_at && Date.parse(r.deleted_at) === at)
}

/** The trip's items in the order walked: the order put by hand, else the order they went in. */
export function walkedItems<T extends { id: string; done_at: string | null }>(bought: T[], order: readonly string[] | null | undefined): T[] {
  const at = new Map((order ?? []).map((id, i) => [id, i]))
  const byTick = [...bought].sort((a, b) => (a.done_at ?? '').localeCompare(b.done_at ?? ''))
  if (!at.size) return byTick
  return [
    ...byTick.filter(b => at.has(b.id)).sort((a, b) => at.get(a.id)! - at.get(b.id)!),
    ...byTick.filter(b => !at.has(b.id)),
  ]
}

/** "23:41", or "1:05:12" past the hour — a stopwatch, never "23 min". */
export function clock(ms: number): string {
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** The one line against the usual trip, or null when there is no usual yet. */
export function versusUsual(r: Pick<TripRecap, 'ms' | 'usualMs'>): string | null {
  if (r.ms === null || r.usualMs === null) return null
  const minutes = Math.round((r.ms - r.usualMs) / 60_000)
  if (minutes === 0) return 'About as long as your usual trip.'
  const n = Math.abs(minutes)
  return `${n} min ${minutes < 0 ? 'faster' : 'longer'} than your usual trip.`
}
